import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import BottomNav from '../../components/BottomNav';
import './detail.less';

interface Event {
  id: number;
  eventName: string;
  startTime: string;
  endTime: string;
  requiredContribution: number;
  currentContribution: number;
  status: 'SUCCESS' | 'FAILED' | 'IN_PROGRESS';
  experienceReward: number;
  levelRequirement: number;
  coverImage: string;
  allowedTeams: any[];
  achievements: any[];
  participants: any[];
}

const EventDetailPage: React.FC = () => {
  const { id, hash } = useParams<{ id: string; hash: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [participating, setParticipating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`/api/v1/backroomsteamcard/events/${id}`);
        if (!response.ok) {
          throw new Error('Event not found');
        }
        const data = await response.json();
        setEvent(data);
      } catch (error) {
        console.error('Failed to fetch event:', error);
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, navigate]);

  const handleParticipate = async () => {
    if (!hash || !event) return;

    setParticipating(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/backroomsteamcard/events/${event.id}/participate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ hash }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '参与失败');
      }

      setEvent(data);
      message.success('参与成功！');
    } catch (error: any) {
      setError(error.message);
      message.error(error.message);
    } finally {
      setParticipating(false);
    }
  };

  const isParticipated = event?.participants?.some(p => p.hash === hash);
  const canParticipate = event?.status === 'IN_PROGRESS' && !isParticipated;

  if (loading) {
    return (
      <div className="event-detail-page">
        <div className="content">
          <div className="global-loading">加载中...</div>
        </div>
        <BottomNav hash={hash} />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-page">
        <div className="content">
          <div className="error">事件不存在</div>
        </div>
        <BottomNav hash={hash} />
      </div>
    );
  }

  return (
    <div className="event-detail-page">
      <div className="back-button" onClick={() => navigate(`/events/${hash}`)}>
        &lt; 返回事件大厅
      </div>

      {event.status === 'IN_PROGRESS' ?
        <div className="progress-section">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${Math.min(100, (event.currentContribution / event.requiredContribution) * 100)}%`
              }}
            />
          </div>
          <div className="progress-text">
            {event.currentContribution} / {event.requiredContribution}
          </div>
        </div> : null}

      <div className="content">


        <div className="event-cover">
          <img src={'https://oss.usecubes.cn/backrooms_team/events/' + event.id + '.jpg'} alt={event.eventName} />
        </div>


        <div className="event-info">

          <div className="info-section">
            <h1 className="event-title">{event.eventName}</h1>
          </div>

          {event.allowedTeams?.length > 0 && (
            <div className="info-section">
              <div className="title">团队：</div>
              <div className="info-item">
                {event.allowedTeams.map(team => (
                  <div key={team.id} className="team-item">
                    {team.teamName}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="info-section">
            <div className="title">时间：</div>
            <div className="info-item">
              <span>{new Date(event.startTime).toLocaleDateString()} - {new Date(event.endTime).toLocaleDateString()}</span>
            </div>
          </div>

          {event.achievements?.length > 0 && (
            <div className="info-section">
              <div className="title">可获得成就：</div>
              <div className="info-item">
                {event.achievements.map(achievement => (
                  <div key={achievement.id} className="achievement-item">
                    {achievement.achievementName}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="info-section">
            <div className="title">达成条件：</div>
            <div className="info-item">
              <span>时间范围内达到{event.requiredContribution}贡献值</span>
            </div>
          </div>
        </div>
        <span className={`tag ${event.status === 'SUCCESS' ? 'status-success' : event.status === 'FAILED' ? 'status-failed' : 'status-in_progress'}`}>
          {event.status === 'SUCCESS' ? '成功' : event.status === 'FAILED' ? '失败' : '进行中'}
        </span>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}



      </div>

      {hash && event.status === 'IN_PROGRESS' && (
        <div className="participate-section">
          {isParticipated ? (
            <div className="participated-message">
              您已参与此事件
            </div>
          ) : event.status !== 'IN_PROGRESS' ? (
            <div className="event-ended-message">
              事件已结束
            </div>
          ) : (
            <div
              className={`participate-button ${participating ? 'loading' : ''}`}
              onClick={handleParticipate}
            // disabled={participating || !canParticipate}
            >
              {participating ? '参与中...' : '参与事件并助力'}
            </div>
          )}
        </div>
      )}

      <BottomNav hash={hash} />
    </div>
  );
};

export default EventDetailPage; 